// UserWaypointDetail — mini-card shown when the pilot taps a user
// waypoint on the map. Lets them add to plan, rename, or delete.

import { useState } from 'react';
import { X, Pencil, Trash2, Check } from '../shell/Icon';
import { useWaypointStore } from '../stores/waypointStore';
import InsertIntoRouteButtons from '../components/route/InsertIntoRouteButtons';

export interface UserWaypointSeed {
  id: string;
  name: string;
  lat: number;
  lon: number;
  notes?: string;
}

interface Props {
  waypoint: UserWaypointSeed;
  onClose: () => void;
}

export default function UserWaypointDetail({ waypoint, onClose }: Props) {
  const { update, remove } = useWaypointStore();
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(waypoint.name);
  const [deleting, setDeleting] = useState(false);

  async function handleRename() {
    if (!newName.trim()) return;
    await update(waypoint.id, { name: newName.trim() });
    setRenaming(false);
    onClose();
  }

  async function handleDelete() {
    setDeleting(true);
    await remove(waypoint.id);
    onClose();
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 pointer-events-none flex justify-center pb-safe"
      role="dialog"
      aria-label="Waypoint detail"
    >
      <div className="pointer-events-auto w-full max-w-sm m-3 bg-cockpit-800/97 backdrop-blur border border-cockpit-600 rounded-2xl shadow-rail overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 flex items-center justify-between border-b border-cockpit-700">
          <div>
            <p className="text-xs text-cockpit-400 uppercase tracking-wider mb-0.5">User Waypoint</p>
            <p className="text-base font-bold text-ember-400 font-mono">{waypoint.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-cockpit-400 hover:text-cockpit-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Coords */}
        <div className="px-4 py-2 border-b border-cockpit-700">
          <p className="text-xs text-cockpit-400 font-mono">
            {Math.abs(waypoint.lat).toFixed(4)}°{waypoint.lat >= 0 ? 'N' : 'S'}{' '}
            {Math.abs(waypoint.lon).toFixed(4)}°{waypoint.lon >= 0 ? 'E' : 'W'}
          </p>
          {waypoint.notes && (
            <p className="text-xs text-cockpit-300 mt-0.5">{waypoint.notes}</p>
          )}
        </div>

        {/* Rename prompt */}
        {renaming ? (
          <div className="px-4 py-3 border-b border-cockpit-700 space-y-2">
            <p className="text-xs text-cockpit-400">New name</p>
            <div className="flex gap-2">
              <input
                autoFocus
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRename();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                className="flex-1 bg-cockpit-900 border border-cockpit-600 rounded px-2 py-1 text-sm text-cockpit-100 focus:outline-none focus:border-ember-500"
              />
              <button
                type="button"
                onClick={() => void handleRename()}
                className="px-3 py-1 rounded bg-ember-600 hover:bg-ember-500 text-xs text-white"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => setRenaming(false)}
                className="px-2 py-1 rounded border border-cockpit-600 text-xs text-cockpit-300 hover:text-cockpit-100"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ) : null}

        {/* Actions */}
        <div className="px-4 py-3 space-y-2">
          <InsertIntoRouteButtons
            point={{ ident: waypoint.name, lat: waypoint.lat, lon: waypoint.lon, kind: 'user' }}
            onDone={onClose}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { setRenaming(true); setNewName(waypoint.name); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-cockpit-600 text-cockpit-300 hover:text-cockpit-100 hover:border-cockpit-500 transition-colors text-sm"
              aria-label="Rename waypoint"
            >
              <Pencil size={15} />
              Rename
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-800 text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-40 text-sm"
              aria-label="Delete waypoint"
            >
              <Trash2 size={15} />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
